// QA 問答 API（使用 qaRequest，帶 Bearer Token）
import qaRequest from '../core/qaRequest';

/**
 * 取得單一問題詳細內容
 * @param {number|string} id - 問題 ID
 * @returns {Promise<QuestionDetailResponse>}
 * @see {@link ./types.js} QuestionDetailResponse
 */
const apiGetQuestion = (id) => qaRequest.get(`/v1/questions/${id}`);

/**
 * 取得指定講座的問題列表
 * @param {number|string} videoCourseId - 影音課程 ID
 * @param {number|string} courseId - 單元（lecture）ID
 * @param {number} [page=1] - 頁數
 * @returns {Promise<QuestionListResponse>}
 * @see {@link ./types.js} QuestionListResponse
 */
const apiGetLectureQuestions = (videoCourseId, courseId, page = 1) => qaRequest.get(`/v1/questions/video-courses/${videoCourseId}/lectures/${courseId}?page=${page}`);

/**
 * 取得整個影音課程最新 20 筆問題
 * @param {number|string} videoCourseId - 影音課程 ID
 * @returns {Promise<QuestionListResponse>}
 * @see {@link ./types.js} QuestionListResponse
 */
const apiGetVideoCourseQuestions = (videoCourseId) => qaRequest.get(`/v1/questions/video-courses/${videoCourseId}`);

/**
 * 取得發問歷史紀錄
 * @param {number} [page=1] - 頁數
 * @returns {Promise<QuestionHistoryResponse>}
 * @see {@link ./types.js} QuestionHistoryResponse
 */
const apiGetQuestionHistory = (page = 1) => qaRequest.get(`/v1/questions/history?page=${page}`);

/**
 * 取得問題分類選單
 * @returns {Promise<QuestionTypesResponse>}
 * @see {@link ./types.js} QuestionTypesResponse
 */
const apiGetQuestionTypes = () => qaRequest.get('/v1/questions/selector/types');

/**
 * 在指定單元下新增問題
 * @param {number|string} courseId - 單元（lecture）ID
 * @param {{ title: string, question_type_id: number, body: string }} payload - 問題內容
 * @returns {Promise<MessageResponse>}
 * @see {@link ../core/types.js} MessageResponse
 */
const apiPostQuestion = (courseId, payload) => qaRequest.post(`/v1/questions/lectures/${courseId}`, payload);

/**
 * 編輯問題
 * @param {number|string} id - 問題 ID
 * @param {{ title: string, question_type_id: number, body: string }} payload - 問題內容
 * @returns {Promise<MessageResponse>}
 * @see {@link ../core/types.js} MessageResponse
 */
const apiPutQuestion = (id, payload) => qaRequest.put(`/v1/questions/${id}`, payload);

/**
 * 刪除問題
 * @param {number|string} id - 問題 ID
 * @returns {Promise<MessageResponse>}
 * @see {@link ../core/types.js} MessageResponse
 */
const apiDeleteQuestion = (id) => qaRequest.delete(`/v1/questions/${id}`);

/**
 * 對問題按讚 / 取消讚
 * @param {'post'|'delete'} method - post 按讚，delete 取消讚
 * @param {number|string} questionId - 問題 ID
 * @returns {Promise<MessageResponse>}
 * @see {@link ../core/types.js} MessageResponse
 */
const apiToggleQuestionUpvote = (method, questionId) => qaRequest[method](`/v1/questions/${questionId}/upvote`);

/**
 * 新增回覆
 * @param {number|string} questionId - 問題 ID
 * @param {string} body - 回覆內容（HTML）
 * @returns {Promise<MessageResponse>}
 * @see {@link ../core/types.js} MessageResponse
 */
const apiPostReply = (questionId, body) => qaRequest.post(`/v1/questions/${questionId}/replies`, { body });

/**
 * 編輯回覆
 * @param {number|string} questionId - 問題 ID
 * @param {number|string} replyId - 回覆 ID
 * @param {string} body - 回覆內容（HTML）
 * @returns {Promise<MessageResponse>}
 * @see {@link ../core/types.js} MessageResponse
 */
const apiPutReply = (questionId, replyId, body) => qaRequest.put(`/v1/questions/${questionId}/replies/${replyId}`, { body });

/**
 * 刪除回覆
 * @param {number|string} questionId - 問題 ID
 * @param {number|string} replyId - 回覆 ID
 * @returns {Promise<MessageResponse>}
 * @see {@link ../core/types.js} MessageResponse
 */
const apiDeleteReply = (questionId, replyId) => qaRequest.delete(`/v1/questions/${questionId}/replies/${replyId}`);

/**
 * 對回覆按讚 / 取消讚
 * @param {'post'|'delete'} method - post 按讚，delete 取消讚
 * @param {number|string} questionId - 問題 ID
 * @param {number|string} replyId - 回覆 ID
 * @returns {Promise<MessageResponse>}
 * @see {@link ../core/types.js} MessageResponse
 */
const apiToggleReplyUpvote = (method, questionId, replyId) => qaRequest[method](`/v1/questions/${questionId}/replies/${replyId}/upvote`);

/**
 * 設定 / 取消回覆為最佳解答
 * @param {'post'|'delete'} method - post 設為最佳解答，delete 取消
 * @param {number|string} questionId - 問題 ID
 * @param {number|string} replyId - 回覆 ID
 * @returns {Promise<MessageResponse>}
 * @see {@link ../core/types.js} MessageResponse
 */
const apiToggleReplyBestAnswer = (method, questionId, replyId) => qaRequest[method](`/v1/questions/${questionId}/replies/${replyId}/best-answer`);

/**
 * 取得圖片上傳用的預簽章網址
 * @param {string} ext - 圖片副檔名（png / jpeg）
 * @returns {Promise<ImageUploadUrlResponse>}
 * @see {@link ./types.js} ImageUploadUrlResponse
 */
const apiGetImageUploadUrl = (ext) => qaRequest.get(`/v1/questions/upload-image-url/${ext}`);

/**
 * 取得評論預覽資料。網址由站內訊息內容夾帶（絕對網址），故直接傳入完整 URL。
 * @param {string} url - 訊息內容中夾帶的完整資料網址
 * @returns {Promise<*>}
 */
const apiGetReviewData = (url) => qaRequest.get(url);

export default {
  apiGetQuestion,
  apiGetLectureQuestions,
  apiGetVideoCourseQuestions,
  apiGetQuestionHistory,
  apiGetQuestionTypes,
  apiPostQuestion,
  apiPutQuestion,
  apiDeleteQuestion,
  apiToggleQuestionUpvote,
  apiPostReply,
  apiPutReply,
  apiDeleteReply,
  apiToggleReplyUpvote,
  apiToggleReplyBestAnswer,
  apiGetImageUploadUrl,
  apiGetReviewData,
};
